// La card de un proyecto en /proyectos (panel postventa v2, README §1).
//
// Toda la card es un enlace al proyecto. Lo que contesta de un vistazo
// es «¿este proyecto requiere una acción de la inmobiliaria?»: la pill
// de cabecera y la celda «Pendientes de gestión» se pintan sólo con lo
// que espera al jefe (por aceptar + por asignar); lo que está en manos
// del contratista o del propietario va al pie, en calma.
import { Link } from "react-router-dom";
import type { CarteraDashboardGroupItem } from "@bitacorapro/api-types";
import { pendientesDeGestion } from "../lib/postventa/phase";
import { lastSeenLabel } from "../lib/util";
import { Icon } from "./icons";

type Props = {
  p: CarteraDashboardGroupItem;
};

export function ProjCard({ p }: Props) {
  const total = p.assetCount;
  const active = p.activeAssetCount ?? 0;
  const pendingTriage = p.pendingTriage ?? 0;
  const unassigned = p.unassignedObservations ?? 0;
  const pendientes = pendientesDeGestion(p);
  const withAssignee = p.withAssignee ?? 0;
  const awaiting = p.awaitingConformity ?? 0;
  // La actividad que importa acá es la de OBSERVACIONES; `lastActivityAt`
  // es la última acta firmada y sólo sirve de respaldo cuando no hubo
  // ninguna observación todavía.
  const actividad = p.lastObservationAt ?? p.lastActivityAt;
  const pct = total > 0 ? Math.round((active / total) * 100) : 0;
  const addr = p.subline ?? "";

  // Nombre accesible corto: el enlace no debe leer los seis números de la
  // card. Nombre, dirección y el veredicto.
  const veredicto =
    pendientes > 0
      ? `Requiere gestión: ${pendientes} ${pendientes === 1 ? "pendiente" : "pendientes"} de gestión.`
      : "Al día, sin pendientes.";
  const ariaLabel = `${p.name}. ${addr ? `${addr}. ` : ""}${veredicto}`;

  return (
    <Link to={`/proyectos/${p.id}`} className="proj-card" aria-label={ariaLabel}>
      <div className="proj-card__head">
        <div className="proj-card__lead">
          <div className="proj-card__name">{p.name}</div>
          <div className="proj-card__addr">{addr || "—"}</div>
        </div>
        {pendientes > 0 ? (
          <span className="st-pill st-pill--review">Requiere gestión</span>
        ) : (
          <span className="st-pill st-pill--ok">Al día</span>
        )}
      </div>
      <div className="proj-card__body">
        <div className="proj-card__cell">
          <span className="proj-card__k">Unidades activas</span>
          <span className="proj-card__v">
            {active}
            <small>de {total}</small>
          </span>
          <div className="bar-track" aria-hidden="true">
            <div className="bar-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div
          className={`proj-card__cell ${pendientes > 0 ? "proj-card__cell--pend" : "proj-card__cell--calm"}`}
        >
          <span className="proj-card__k">Pendientes de gestión</span>
          <span className="proj-card__v">{pendientes}</span>
          <div className="proj-card__break">
            {pendientes > 0 ? (
              <>
                <span>
                  Por aceptar <b>{pendingTriage}</b>
                </span>
                <span>
                  Por asignar <b>{unassigned}</b>
                </span>
              </>
            ) : (
              "Nada espera acción de la inmobiliaria"
            )}
          </div>
        </div>
      </div>
      <div className="proj-card__foot">
        <span>
          <b>{p.openObservations}</b> abiertas
        </span>
        <span>
          <b>{withAssignee}</b> con contratista
        </span>
        <span>
          <b>{awaiting}</b> listas para revisar
        </span>
        <span className="proj-card__foot-end">
          {actividad ? lastSeenLabel(actividad) : "Sin actividad"}
          <Icon.ChevronRight s={14} />
        </span>
      </div>
    </Link>
  );
}
